/**
 * PDP behaviours — companion to 13l-pdp.css. One file for the small pieces of PDP interactivity
 * the theme doesn't ship, so they don't sprawl into one asset per feature.
 *
 * 1. Variant swatch hover — hovering a color swatch previews that variant's name in the option
 *    header (the "Color: ..." span in .variant-picker__option-info) without selecting it.
 * 2. Gallery thumbnail carets — the rail's up/down arrows disable at the first/last media.
 * 3. Mobile gallery dots — the strip shows four at a time and rolls to follow the current media.
 * 4. Back in Stock "Notify me" button — swaps the disabled "Sold out" button for a BIS trigger.
 */

/* --------------------------------------------------------------------------------------------------------------------
   1. VARIANT SWATCH HOVER
   Mouse out restores the selected value's name. Desktop only — touch devices have no hover and keep the
   tap-to-select behaviour. Delegated on document so it survives <product-rerender> swapping the section's DOM on
   variant change (a rerender replaces the nodes, which also throws away the stashed original text — so a click
   mid-hover can never leave a stale preview behind).
   ----------------------------------------------------------------------------------------------------------------- */
(() => {
  if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

  const SWATCH_SELECTOR = '.shopify-section--main-product .variant-picker label.color-swatch';

  /* The value span sits beside the <legend> in the option's header row. */
  const valueSpanFor = (label) => {
    const fieldset = label.closest('.variant-picker__option');
    return fieldset && fieldset.querySelector('.variant-picker__option-info .h-stack > span');
  };

  const hoveredName = (label) => {
    const name = label.querySelector('.sr-only');
    return name ? name.textContent.trim() : '';
  };

  document.addEventListener(
    'mouseover',
    (event) => {
      const label = event.target.closest(SWATCH_SELECTOR);
      if (!label) return;

      const span = valueSpanFor(label);
      const name = hoveredName(label);
      if (!span || !name) return;

      if (!('selectedValue' in span.dataset)) span.dataset.selectedValue = span.textContent;
      span.textContent = name;
    },
    { passive: true }
  );

  document.addEventListener(
    'mouseout',
    (event) => {
      const label = event.target.closest(SWATCH_SELECTOR);
      if (!label) return;

      const span = valueSpanFor(label);
      if (!span || !('selectedValue' in span.dataset)) return;

      span.textContent = span.dataset.selectedValue;
      delete span.dataset.selectedValue;
    },
    { passive: true }
  );
})();

/* --------------------------------------------------------------------------------------------------------------------
   2. GALLERY THUMBNAIL CARETS — disabled at the ends
   The theme's scroll-carousel never actually loops (next()/previous() clamp at the last/first cell), but its own
   carousel-prev/next-button disable hangs off scroll:edge-nearing events measured against a 100px scrollLeft
   threshold — a heuristic that can miss (initial state, programmatic jumps, rerenders) and leaves the arrows
   looking clickable at the ends, which is what read as an infinite loop. This drives the same buttons from the
   carousel's selected index instead, which is exact: first media -> up disabled, last media -> down disabled.

   carousel:change does not bubble; the document listener works because capture-phase listeners run for
   non-bubbling events too. That also makes it survive <product-rerender> DOM swaps for free.
   ----------------------------------------------------------------------------------------------------------------- */
(() => {
  const CAROUSEL_SELECTOR = '.shopify-section--main-product .product-gallery__carousel';

  const sync = (carousel, index) => {
    const last = carousel.cells ? carousel.cells.length - 1 : 0;
    const prev = document.querySelector('carousel-prev-button[aria-controls="' + carousel.id + '"] > button');
    const next = document.querySelector('carousel-next-button[aria-controls="' + carousel.id + '"] > button');

    if (prev) prev.disabled = index <= 0;
    if (next) next.disabled = index >= last;
  };

  document.addEventListener(
    'carousel:change',
    (event) => {
      const carousel = event.target;
      if (!(carousel instanceof Element) || !carousel.matches(CAROUSEL_SELECTOR)) return;
      sync(carousel, event.detail.index);
    },
    { capture: true, passive: true }
  );

  /* First paint (and any rerender that lands back on the first media) never fires carousel:change, so settle the
     initial state directly — covers the single-image product where "down" would otherwise sit enabled forever. */
  const initial = () => {
    document.querySelectorAll(CAROUSEL_SELECTOR).forEach((carousel) => {
      if (typeof carousel.selectedIndex === 'number') sync(carousel, carousel.selectedIndex);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initial, { once: true });
  } else {
    initial();
  }
})();

/* --------------------------------------------------------------------------------------------------------------------
   3. MOBILE GALLERY DOTS — a rolling window of four
   13l-pdp.css caps the dot strip at four dots' worth of width and hides its overflow. Every dot stays in the DOM
   (so the count still reads correctly to assistive tech and every media stays reachable) — this just keeps the
   current one inside the window by scrolling the strip, which is why the extra dots are clipped rather than
   removed.

   Triggered by the dots' own aria-current rather than by the carousel's carousel:change, which is the one thing
   here that was worth measuring: aria-current moves as soon as the gallery scrolls, while carousel:change is not
   dispatched until the scroll settles. Following the event left the window a full media behind on every swipe —
   the current dot would sit outside it until the animation finished. aria-current is also, by definition,
   whichever dot the shopper sees highlighted, so it is the correct thing to follow.

   The observer is attached to the SECTION, not the strip, with subtree: true — <product-rerender> replaces the
   markup inside the section on variant change, which would throw away an observer bound to the strip itself.

   The window rolls by whole dots rather than centring the current one. Centring reads nicer in theory but lands
   the strip on fractional offsets, which leaves a dot clipped in half at each edge — so what you actually see is
   three whole dots and two slivers, not four. Stepping in multiples of the pitch keeps the leftmost dot flush
   with the window edge, so exactly four are ever visible and the roll advances one dot at a time as the current
   media passes the edge — and because the strip only ever sits on exact multiples, reading scrollLeft back to
   find the current leftmost dot stays precise.

   The roll is instant by design: Chrome drops animated scrolls on the overflow: hidden scrollport the strip uses
   (see the note in 13l-pdp.css), so a smooth scroll-behavior would silently make this a no-op.
   ----------------------------------------------------------------------------------------------------------------- */
(() => {
  const SECTION_SELECTOR = '.shopify-section--main-product';
  const STRIP_SELECTOR = '.page-dots';

  const roll = (strip) => {
    /* A filtered variant hides its dot and its carousel cell off the same filtered_indexes list
       (product-gallery.liquid), so the dots left standing are the ones that map to real media. */
    const dots = Array.from(strip.children).filter((dot) => !dot.hasAttribute('hidden'));
    if (dots.length < 2) return;

    const index = dots.findIndex((dot) => dot.getAttribute('aria-current') === 'true');
    if (index < 0) return;

    /* Measured off the rendered row instead of hardcoding the stylesheet's numbers, so moving --dot-size or the
       window width in CSS cannot desync this. clientWidth is 0 while the strip is display: none (desktop hides
       it), which falls through to the reset below. */
    const pitch = dots[1].offsetLeft - dots[0].offsetLeft;
    if (pitch <= 0) return;

    const gap = pitch - dots[0].offsetWidth;
    const perView = Math.floor((strip.clientWidth + gap) / pitch);

    /* Everything already fits: no window to roll. */
    if (perView < 1 || perView >= dots.length) {
      strip.scrollLeft = 0;
      return;
    }

    let leftmost = Math.round(strip.scrollLeft / pitch);
    if (index < leftmost) leftmost = index;
    else if (index > leftmost + perView - 1) leftmost = index - perView + 1;

    strip.scrollLeft = Math.min(Math.max(leftmost, 0), dots.length - perView) * pitch;
  };

  /* Rolling never touches aria-current, so this cannot feed itself. */
  const observer = new MutationObserver((records) => {
    const strips = new Set();

    records.forEach((record) => {
      const strip = record.target.parentElement;
      if (strip && strip.matches(STRIP_SELECTOR)) strips.add(strip);
    });

    strips.forEach(roll);
  });

  /* Also settles the initial position: a rerender can land on a media other than the first, which would leave
     the window showing dot 1 while a later one is current. */
  const initial = () => {
    document.querySelectorAll(SECTION_SELECTOR).forEach((section) => {
      observer.observe(section, { subtree: true, attributes: true, attributeFilter: ['aria-current'] });
      section.querySelectorAll(STRIP_SELECTOR).forEach(roll);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initial, { once: true });
  } else {
    initial();
  }
})();

/* --------------------------------------------------------------------------------------------------------------------
   4. BACK IN STOCK — "Notify me" button
   The Back in Stock app expects the theme to build and place its own trigger button next to Add to cart; it
   only ships the popup/email-capture UI itself (window.BIS, injected by the app's own script tag). The legacy
   PDP (sections/product-template.liquid) already wires this up against its own markup — this is the same idea
   against the new product-info block markup, driven by the theme's own variant:change event instead of BIS's
   generic change/popover-click polling, since Horizon already tells us exactly which variant is selected and
   whether it's available.
   ----------------------------------------------------------------------------------------------------------------- */
(() => {
  const SECTION_SELECTOR = '.shopify-section--main-product';

  const wireForm = (form) => {
    const anchor = form.querySelector('[data-block-type="buy-buttons"] .button[name="add"]');
    if (!anchor || anchor.dataset.bisWired) return;
    anchor.dataset.bisWired = 'true';

    const button = document.createElement('button');
    button.type = 'button';
    button.id = 'BIS_trigger'; /* the app's own script opens its popup off this exact id, same as the legacy PDP */
    /* Cloning the anchor's classes would also copy button--outline: the "Sold out" button renders outline-style
       (buy-buttons.liquid sets atc_button_style to 'outline' whenever the variant is unavailable), but the
       Notify me mock is the theme's solid/primary button, same as a normal in-stock Add to cart. */
    button.className = anchor.className.replace(/\bbutton--outline\b/g, '').replace(/\s+/g, ' ').trim();
    /* snippets/button.liquid writes --button-background / --button-text-color inline when the block has custom
       colors set (buy-buttons.liquid's atc_button_background / atc_button_text_color) — className alone drops
       those, which left the hover wipe animation resolving the wrong colors (looked like the border vanished). */
    const anchorStyle = anchor.getAttribute('style');
    if (anchorStyle) button.setAttribute('style', anchorStyle);
    button.textContent = (window.BIS && BIS.Config && BIS.Config.button && BIS.Config.button.caption) || 'Notify me';
    button.hidden = true;
    anchor.insertAdjacentElement('afterend', button);

    const idInput = form.querySelector('input[name="id"]');
    let variantId = idInput ? idInput.value : null;

    const sync = (variant) => {
      const unavailable = variant ? !variant.available : anchor.disabled;
      if (variant) variantId = variant.id;
      button.hidden = !unavailable;
      anchor.hidden = unavailable;
    };

    button.addEventListener('click', () => {
      if (window.BIS && BIS.popup && variantId) BIS.popup.form.selectVariant(variantId);
    });

    form.addEventListener('variant:change', (event) => sync(event.detail.variant));
    sync(null);
  };

  const start = () => {
    document.querySelectorAll(SECTION_SELECTOR + ' form.shopify-product-form').forEach(wireForm);
  };

  /* Exits quietly when the app is uninstalled/disabled (window.BIS never appears) or the current product has no
     variants under Back in Stock tracking — the same two early-outs the legacy script uses. */
  const waitForBIS = (attemptsLeft) => {
    if (window.BIS && BIS.popup && BIS.popup.ready) {
      BIS.popup.ready.then(() => {
        if (BIS.popup.variants && BIS.popup.variants.length > 0) start();
      });
    } else if (attemptsLeft > 0) {
      setTimeout(() => waitForBIS(attemptsLeft - 1), 250);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => waitForBIS(40), { once: true });
  } else {
    waitForBIS(40);
  }
})();
